import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Users,
  HeartHandshake,
  MessagesSquare,
  Eye,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/site/SectionHeader";
import { BlobA, BlobB, BlobC, SoftLine } from "@/components/site/BlobBackground";
import FounderCard from "@/components/site/FounderCard";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import FAQList from "@/components/site/FAQList";
import SocialFeedPlaceholder from "@/components/site/SocialFeedPlaceholder";
import NewsletterForm from "@/components/site/NewsletterForm";
import TrackCard from "@/components/site/TrackCard";
import PhilosophyList from "@/components/site/PhilosophyList";
import {
  SITE,
  ORG_STATS,
  PILLARS,
  FOUNDERS,
  FOUNDATION_TRACK,
  PRACTICE_TRACK,
  FAQS_SITE,
} from "@/lib/siteConfig";

const PILLAR_ICONS = [
  <Eye className="w-6 h-6" />,
  <MessagesSquare className="w-6 h-6" />,
  <HeartHandshake className="w-6 h-6" />,
  <Users className="w-6 h-6" />,
  <Award className="w-6 h-6" />,
];

export default function HomePage() {
  const reduced = useReducedMotion();

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden hero-wash bg-grain">
        <BlobA className="absolute -top-24 -left-20 w-[420px] h-[420px] hidden sm:block" />
        <BlobB className="absolute -top-10 -right-24 w-[460px] h-[460px] hidden sm:block" />
        <div className="container-narrow relative pt-14 sm:pt-20 lg:pt-28 pb-16 sm:pb-24 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={reduced ? false : { opacity: 0, y: 10 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(126,155,134,0.18)] text-moss text-xs font-semibold tracking-wide"
                data-testid="hero-badge"
              >
                <Sparkles className="w-3.5 h-3.5" /> Built on 8 years of Therapist Tribe
              </motion.div>

              <motion.h1
                initial={reduced ? false : { opacity: 0, y: 16 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                data-testid="hero-title"
                className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-ink"
              >
                The real{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-terracotta">human experiences..</span>
                  <SoftLine className="absolute -bottom-2 left-0 w-full h-3 z-0" />
                </span>
              </motion.h1>

              <motion.p
                initial={reduced ? false : { opacity: 0, y: 16 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 text-ink-muted text-base sm:text-lg leading-[1.75] max-w-xl"
                data-testid="hero-subtitle"
              >
                The Human Work Project is a practice-oriented psychology program bringing together
                <span className="text-ink font-medium"> training, practice, and supervision</span>.
                Designed and facilitated by practising therapists and clinical psychologists — because
                before you help someone, you learn how to be with them.
              </motion.p>

              <motion.div
                initial={reduced ? false : { opacity: 0, y: 12 }}
                animate={reduced ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <Link to="/course">
                  <Button
                    data-testid="hero-primary-cta-button"
                    className="bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl px-6 py-6 text-base transition-colors"
                  >
                    Foundation Track <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/practice-track">
                  <Button
                    data-testid="hero-secondary-cta-button"
                    className="bg-[#5F7F6B] hover:bg-[#506e5c] text-white rounded-xl px-6 py-6 text-base transition-colors"
                  >
                    Advanced Practice Track
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={reduced ? false : { opacity: 0 }}
                animate={reduced ? undefined : { opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3"
              >
                {ORG_STATS.map((s) => (
                  <Stat key={s.l} n={s.n} l={s.l} />
                ))}
              </motion.div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={reduced ? false : { opacity: 0, scale: 0.96 }}
                animate={reduced ? undefined : { opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[4/5] sm:aspect-square w-full max-w-md mx-auto"
              >
                <BlobC className="absolute inset-0 w-full h-full" />
                <div className="absolute inset-6 rounded-[28px] bg-paper border border-[rgba(74,63,57,0.10)] shadow-[0_14px_40px_rgba(31,26,23,0.10)] p-6 flex flex-col">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold">
                    Applications open · Cohort 02
                  </div>
                  <h3 className="mt-2 font-display text-xl sm:text-2xl text-ink leading-snug">
                    Training · Practice · Supervision
                  </h3>
                  <p className="mt-2 italic font-display text-terracotta text-[15px]">
                    {SITE.pullQuote}
                  </p>
                  <div className="mt-5 space-y-3 text-sm text-ink">
                    <p className="text-ink-muted">Two tracks. One cohort begins 1 December 2026.</p>
                    <CardRow
                      title="Foundation Track"
                      sub="1 Dec 2026 – 1 Jan 2027 · 80+ hrs"
                    />
                    <CardRow
                      title="Advanced Practice Track"
                      sub="1 Dec 2026 – 28 Feb 2027 · 75+ hrs"
                    />
                  </div>
                  <div className="mt-auto pt-5 text-xs text-ink-muted">
                    Hybrid · Limited seats · Selection-based
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAM HIGHLIGHTS / 5 PILLARS */}
      <section className="container-narrow py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="What we bring together"
          title="Psychology learned the way it's actually practised."
          subtitle="Five things, woven into every cohort — so theory becomes lived skill, held by supervision and reflection."
          testidPrefix="pillars"
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, idx) => (
            <PillarCard key={p.title} icon={PILLAR_ICONS[idx]} title={p.title} text={p.text} />
          ))}
        </div>
      </section>

      {/* TWO TRACKS */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20 lg:py-24">
          <SectionHeader
            eyebrow="Cohort 02 · Starts 1 December 2026"
            title="Choose the track that meets you where you are."
            subtitle="Two parallel programs — one for those starting out, one for those moving from classroom to clinic."
            testidPrefix="tracks"
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TrackCard
              track={FOUNDATION_TRACK}
              href="/course"
              ctaLabel="Explore Foundation Track"
              accent="terracotta"
            />
            <TrackCard
              track={PRACTICE_TRACK}
              href="/practice-track"
              ctaLabel="Explore Advanced Practice Track"
              accent="sage"
            />
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="container-narrow py-16 sm:py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Program philosophy"
              title="Five quiet beliefs that shape this work."
              subtitle="Everything we design — the modules, the field work, the supervision spaces — sits on these."
              testidPrefix="philosophy"
            />
          </div>
          <div className="lg:col-span-7">
            <PhilosophyList />
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20 lg:py-24">
          <SectionHeader
            eyebrow="Who's behind this"
            title="Led by Astha, built with a small team of practitioners."
            subtitle="The Human Work Project is led by Astha Ahluwalia, with Ruhika Gambhir, Aditi Mukund, and a growing circle of clinicians, educators, and NGO partners shaping the work."
            testidPrefix="founders"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FOUNDERS.map((f) => (
              <FounderCard key={f.slug} founder={f} compact />
            ))}
          </div>
          <div className="mt-8">
            <Link to="/about">
              <Button
                variant="outline"
                data-testid="founders-readmore-button"
                className="rounded-xl border-[rgba(74,63,57,0.18)] hover:bg-[rgba(200,106,74,0.08)] transition-colors"
              >
                Read full bios <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-narrow py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Voices from past cohorts"
          title="Words from learners who've sat in this work."
          subtitle="Reflections from Therapist Tribe alumni — the same team, the same intention, now formally The Human Work Project."
          testidPrefix="testimonials"
        />
        <div className="mt-10">
          <TestimonialsSection />
        </div>
      </section>

      {/* FAQ */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionHeader
                eyebrow="Good questions"
                title="FAQs about The Human Work Project"
                subtitle="Quick answers to the questions we hear most. Can't find what you're looking for? WhatsApp or email us — we'd love to talk."
                testidPrefix="faq-site"
              />
            </div>
            <div className="lg:col-span-7">
              <FAQList items={FAQS_SITE} testidPrefix="faq-site" />
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL FEEDS */}
      <section className="container-narrow py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Stay close"
          title="Follow our work on social."
          subtitle="Reflections, behind-the-scenes from fieldwork, and gentle nudges for your wellbeing."
          testidPrefix="social"
        />
        <div className="mt-10">
          <SocialFeedPlaceholder />
        </div>
      </section>

      {/* CTA BAND */}
      <section className="container-narrow pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-paper p-8 sm:p-12 lg:p-16">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(50% 60% at 80% 20%, rgba(200,106,74,0.6) 0%, rgba(200,106,74,0) 60%), radial-gradient(40% 50% at 10% 80%, rgba(126,155,134,0.45) 0%, rgba(126,155,134,0) 60%)",
            }}
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#F2D7B6] font-semibold">
                Get gentle updates
              </div>
              <h3 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-paper">
                Soft, occasional notes — on programs, practice, and community.
              </h3>
              <p className="mt-4 text-[#E7C2B3] leading-[1.75] max-w-xl">
                No noise. No spam. Just thoughtful updates when we have something worth saying.
              </p>
            </div>
            <div className="lg:col-span-5">
              <NewsletterForm />
              <p className="mt-3 text-xs text-[#E7C2B3]">By subscribing you agree to receive emails from The Human Work Project.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ n, l }) {
  return (
    <div className="bg-paper/70 backdrop-blur-sm rounded-2xl border border-[rgba(74,63,57,0.10)] p-4">
      <div className="font-display text-xl sm:text-2xl lg:text-[26px] text-terracotta leading-none">{n}</div>
      <div className="mt-1 text-xs sm:text-[13px] text-ink-muted leading-snug">{l}</div>
    </div>
  );
}

function CardRow({ title, sub }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
      <div>
        <div className="text-ink font-medium leading-tight">{title}</div>
        <div className="text-xs text-ink-muted leading-snug">{sub}</div>
      </div>
    </div>
  );
}

function PillarCard({ icon, title, text }) {
  return (
    <div className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-6 sm:p-7 hover:shadow-[0_14px_40px_rgba(31,26,23,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow]">
      <div className="w-11 h-11 rounded-xl bg-[rgba(126,155,134,0.18)] text-moss flex items-center justify-center">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-xl text-ink leading-snug">{title}</h3>
      <p className="mt-2 text-ink-muted leading-[1.7] text-[15px]">{text}</p>
    </div>
  );
}
